import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import BlockTitle from './BlockTitle';
import FormLabel from './FormLabel';
import FormInput from './FormInput';
import Button from './Button';
import { resolveVatRate, RESIDENTIAL_VAT_RATE } from '../lib/vat';

const DEFAULT_VAT_RATES = [
  { label: '8%', value: 0.08 },
  { label: '23%', value: 0.23 },
  { label: '0%', value: 0 },
  { label: 'zw.', value: null },
];

const formatPLN = (amount) => {
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount) + ' zl';
};

const formatPLNInvoice = (amount) => {
  const formatted = amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formatted} zl`;
};

const formatDatePolish = (dateStr) => {
  const months = {
    '01': 'stycznia', '02': 'lutego', '03': 'marca', '04': 'kwietnia',
    '05': 'maja', '06': 'czerwca', '07': 'lipca', '08': 'sierpnia',
    '09': 'wrzesnia', '10': 'pazdziernika', '11': 'listopada', '12': 'grudnia',
  };
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(d)} ${months[m] || m} ${y}`;
};

const emptyItem = () => ({
  description: '',
  quantity: 1,
  unit: 'szt.',
  unitPriceNet: 0,
  vatRate: RESIDENTIAL_VAT_RATE,
});

const GenerateInvoiceModal = ({ isOpen, onClose, clientsDetails, flatDetails, investmentDetails }) => {
  const today = new Date().toISOString().split('T')[0];

  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    issueDate: today,
    saleDate: today,
    invoiceType: 'Zaliczkowa',
    sellerName: 'DRUGA - SOVO GROUP Sp. z o.o. Sp. k.',
    sellerAddress: 'ul. Piekary 7, 61-823 Poznan',
    sellerNip: '7781446354',
    buyerName: '',
    buyerAddress: '',
    buyerPesel: '',
    buyerNip: '',
    bankAccount: '',
  });

  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    if (!isOpen) return;

    const client = clientsDetails?.[0];
    const buyer = client?.buyers?.[0];
    const flatCode = flatDetails?.code || 'XX';
    const defaultVat = resolveVatRate(flatDetails);

    setInvoiceData((prev) => ({
      ...prev,
      invoiceNumber: `FV/${new Date().getFullYear()}/${flatCode}/`,
      buyerName: buyer?.name || `${client?.name || ''} ${client?.surname || ''}`.trim() || '',
      buyerAddress: buyer?.address || '',
      buyerPesel: buyer?.pesel || '',
      buyerNip: client?.nip || '',
      bankAccount: flatDetails?.account_number || '12 3456 7890 1234 5678 9012 3456',
    }));

    setItems([{ ...emptyItem(), vatRate: defaultVat }]);
  }, [isOpen, clientsDetails, flatDetails]);

  const handleInvoiceChange = (e) => {
    setInvoiceData({ ...invoiceData, [e.target.name]: e.target.value });
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    if (field === 'quantity' || field === 'unitPriceNet') {
      updated[index][field] = parseFloat(value) || 0;
    } else if (field === 'vatRate') {
      updated[index][field] = value === 'zw' ? null : parseFloat(value);
    } else {
      updated[index][field] = value;
    }
    setItems(updated);
  };

  const addItem = () => {
    const lastItem = items[items.length - 1];
    setItems([...items, { ...emptyItem(), vatRate: lastItem?.vatRate ?? RESIDENTIAL_VAT_RATE }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateItem = (item) => {
    const net = Math.round(item.quantity * item.unitPriceNet * 100) / 100;
    if (item.vatRate === null) {
      return { net, vat: 0, gross: net };
    }
    const vat = Math.round(net * item.vatRate * 100) / 100;
    const gross = Math.round((net + vat) * 100) / 100;
    return { net, vat, gross };
  };

  const totals = items.reduce(
    (acc, item) => {
      const calc = calculateItem(item);
      return { net: acc.net + calc.net, vat: acc.vat + calc.vat, gross: acc.gross + calc.gross };
    },
    { net: 0, vat: 0, gross: 0 }
  );

  const generateHTML = () => {
    const d = invoiceData;
    const titleText = d.invoiceType ? `FAKTURA VAT ${d.invoiceType}` : 'FAKTURA VAT';

    const itemsHtml = items.map((item, i) => {
      const calc = calculateItem(item);
      const vatLabel = item.vatRate === null ? 'zw.' : `${Math.round(item.vatRate * 100)}%`;
      const rowBg = i % 2 === 0 ? 'bg-gray-50' : 'bg-white';
      return `
        <tr class="${rowBg}">
          <td class="py-2 px-2">${i + 1}</td>
          <td class="py-2 px-2 font-medium">${item.description}</td>
          <td class="py-2 px-2 text-center">${item.quantity}</td>
          <td class="py-2 px-2 text-center">${item.unit}</td>
          <td class="py-2 px-2 text-right whitespace-nowrap">${formatPLNInvoice(item.unitPriceNet)}</td>
          <td class="py-2 px-2 text-center">${vatLabel}</td>
          <td class="py-2 px-2 text-right whitespace-nowrap">${formatPLNInvoice(calc.net)}</td>
          <td class="py-2 px-2 text-right whitespace-nowrap">${formatPLNInvoice(calc.gross)}</td>
        </tr>`;
    }).join('');

    const buyerNipHtml = d.buyerNip
      ? `<p class="text-gray-600 mt-2">NIP: ${d.buyerNip}</p>`
      : (d.buyerPesel ? `<p class="text-gray-600 mt-2">PESEL: ${d.buyerPesel}</p>` : '');

    const bankAccount = d.bankAccount || '';

    return `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Faktura ${d.invoiceNumber}</title>
  <script src="https://cdn.tailwindcss.com"><\/script>
  <script>
    tailwind.config = {
      theme: {
        extend: {
          colors: {
            sovo: {
              primary: '#722F37',
              light: '#8B3D47',
              dark: '#5C262E',
            }
          }
        }
      }
    }
  <\/script>
  <style>
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body class="bg-white">
  <div class="bg-white p-8 max-w-[210mm] mx-auto">

    <!-- Header -->
    <div class="flex justify-between items-start mb-8">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">${titleText}</h1>
        <p class="text-xl text-gray-600 mt-1">${d.invoiceNumber}</p>
      </div>
    </div>

    <!-- Daty -->
    <div class="grid grid-cols-3 gap-4 mb-8 text-sm">
      <div class="bg-gray-50 p-3 rounded">
        <p class="text-gray-500">Data wystawienia</p>
        <p class="font-medium">${formatDatePolish(d.issueDate)}</p>
      </div>
      <div class="bg-gray-50 p-3 rounded">
        <p class="text-gray-500">Data sprzedazy</p>
        <p class="font-medium">${formatDatePolish(d.saleDate)}</p>
      </div>
      <div class="bg-gray-50 p-3 rounded">
        <p class="text-gray-500">Sposob zaplaty</p>
        <p class="font-medium">Przelew bankowy</p>
      </div>
    </div>

    <!-- Sprzedawca & Nabywca -->
    <div class="grid grid-cols-2 gap-8 mb-8">
      <div class="flex flex-col">
        <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Sprzedawca</h2>
        <div class="bg-sovo-primary/10 p-4 rounded-lg border-l-4 border-sovo-primary flex-1">
          <p class="font-bold text-gray-900">${d.sellerName}</p>
          <p class="text-gray-600">${d.sellerAddress}</p>
          <p class="text-gray-600 mt-2">NIP: ${d.sellerNip}</p>
        </div>
      </div>
      <div class="flex flex-col">
        <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Nabywca</h2>
        <div class="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-400 flex-1">
          <p class="font-bold text-gray-900">${d.buyerName}</p>
          <p class="text-gray-600">${d.buyerAddress}</p>
          ${buyerNipHtml}
        </div>
      </div>
    </div>

    <!-- Tabela pozycji -->
    <div class="mb-8">
      <table class="w-full text-xs">
        <thead>
          <tr class="bg-sovo-primary text-white">
            <th class="py-2 px-2 text-left rounded-tl-lg w-8">Lp.</th>
            <th class="py-2 px-2 text-left">Nazwa uslugi / towaru</th>
            <th class="py-2 px-2 text-center w-12">Ilosc</th>
            <th class="py-2 px-2 text-center w-12">J.m.</th>
            <th class="py-2 px-2 text-right w-24 whitespace-nowrap">Cena netto</th>
            <th class="py-2 px-2 text-center w-12">VAT</th>
            <th class="py-2 px-2 text-right w-28 whitespace-nowrap">Wartosc netto</th>
            <th class="py-2 px-2 text-right rounded-tr-lg w-28 whitespace-nowrap">Wartosc brutto</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
        </tbody>
      </table>
    </div>

    <!-- Podsumowanie -->
    <div class="flex justify-end mb-8">
      <div class="w-80">
        <div class="flex justify-between py-2 border-b text-sm">
          <span class="text-gray-600">Suma netto:</span>
          <span class="font-medium">${formatPLNInvoice(totals.net)}</span>
        </div>
        <div class="flex justify-between py-2 border-b text-sm">
          <span class="text-gray-600">Suma VAT:</span>
          <span class="font-medium">${formatPLNInvoice(totals.vat)}</span>
        </div>
        <div class="flex justify-between py-3 bg-sovo-primary text-white px-4 rounded-lg mt-2">
          <span class="font-semibold">DO ZAPLATY:</span>
          <span class="font-bold text-lg">${formatPLNInvoice(totals.gross)}</span>
        </div>
      </div>
    </div>

    <!-- Dane do przelewu -->
    <div class="bg-gray-50 p-4 rounded-lg mb-6">
      <h3 class="font-semibold text-gray-900 mb-2">Dane do przelewu</h3>
      <div class="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p class="text-gray-500">Bank</p>
          <p class="font-medium">mBank S.A.</p>
        </div>
        <div>
          <p class="text-gray-500">Numer konta</p>
          <p class="font-medium font-mono">${bankAccount}</p>
        </div>
        <div>
          <p class="text-gray-500">Metoda platnosci</p>
          <p class="font-medium">Przelew bankowy</p>
        </div>
      </div>
    </div>

  </div>
</body>
</html>`;
  };

  const openInvoicePreview = () => {
    const html = generateHTML();
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} wide>
      <div className="p-6">
        <BlockTitle>Generuj fakture</BlockTitle>

        {/* Invoice header */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div>
            <FormLabel htmlFor="invoiceNumber">Numer faktury</FormLabel>
            <FormInput
              id="invoiceNumber"
              name="invoiceNumber"
              value={invoiceData.invoiceNumber}
              onChange={handleInvoiceChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="issueDate">Data wystawienia</FormLabel>
            <FormInput
              id="issueDate"
              name="issueDate"
              type="date"
              value={invoiceData.issueDate}
              onChange={handleInvoiceChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="saleDate">Data sprzedazy</FormLabel>
            <FormInput
              id="saleDate"
              name="saleDate"
              type="date"
              value={invoiceData.saleDate}
              onChange={handleInvoiceChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="invoiceType">Typ faktury</FormLabel>
            <select
              id="invoiceType"
              name="invoiceType"
              className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
              value={invoiceData.invoiceType}
              onChange={handleInvoiceChange}
            >
              <option value="Zaliczkowa">Zaliczkowa</option>
              <option value="">Standardowa</option>
              <option value="Pro forma">Pro forma</option>
            </select>
          </div>
        </div>

        {/* Buyer */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border-l-4 border-gray-400">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Nabywca</div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <FormLabel htmlFor="buyerName">Nazwa</FormLabel>
              <FormInput id="buyerName" name="buyerName" value={invoiceData.buyerName} onChange={handleInvoiceChange} />
            </div>
            <div>
              <FormLabel htmlFor="buyerAddress">Adres</FormLabel>
              <FormInput id="buyerAddress" name="buyerAddress" value={invoiceData.buyerAddress} onChange={handleInvoiceChange} />
            </div>
            <div>
              <FormLabel htmlFor="buyerPesel">PESEL</FormLabel>
              <FormInput id="buyerPesel" name="buyerPesel" value={invoiceData.buyerPesel} onChange={handleInvoiceChange} />
            </div>
            <div>
              <FormLabel htmlFor="buyerNip">NIP</FormLabel>
              <FormInput id="buyerNip" name="buyerNip" value={invoiceData.buyerNip} onChange={handleInvoiceChange} />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs font-semibold text-gray-500 uppercase">Pozycje</div>
            <Button variant="bordered" className="text-xs py-1 px-3" onClick={addItem}>
              + Dodaj pozycje
            </Button>
          </div>

          <div className="space-y-2">
            {items.map((item, index) => {
              const calc = calculateItem(item);
              return (
                <div key={index} className="grid grid-cols-[1fr_70px_70px_120px_90px_100px_100px_30px] gap-2 items-end">
                  <div>
                    {index === 0 && <FormLabel>Opis</FormLabel>}
                    <FormInput
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      placeholder="Opis pozycji"
                    />
                  </div>
                  <div>
                    {index === 0 && <FormLabel>Ilosc</FormLabel>}
                    <FormInput
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      min="1"
                    />
                  </div>
                  <div>
                    {index === 0 && <FormLabel>J.m.</FormLabel>}
                    <FormInput
                      value={item.unit}
                      onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                    />
                  </div>
                  <div>
                    {index === 0 && <FormLabel>Cena netto</FormLabel>}
                    <FormInput
                      type="number"
                      step="0.01"
                      value={item.unitPriceNet}
                      onChange={(e) => handleItemChange(index, 'unitPriceNet', e.target.value)}
                    />
                  </div>
                  <div>
                    {index === 0 && <FormLabel>VAT</FormLabel>}
                    <select
                      className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
                      value={item.vatRate === null ? 'zw' : item.vatRate}
                      onChange={(e) => handleItemChange(index, 'vatRate', e.target.value)}
                    >
                      {DEFAULT_VAT_RATES.map((r) => (
                        <option key={r.label} value={r.value === null ? 'zw' : r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="text-right text-sm py-2">
                    {index === 0 && <div className="text-xs text-gray-500 mb-1">Netto</div>}
                    {formatPLN(calc.net)}
                  </div>
                  <div className="text-right text-sm py-2">
                    {index === 0 && <div className="text-xs text-gray-500 mb-1">Brutto</div>}
                    {formatPLN(calc.gross)}
                  </div>
                  <div>
                    {items.length > 1 && (
                      <button
                        onClick={() => removeItem(index)}
                        className="text-red-400 hover:text-red-600 text-lg pb-2"
                        title="Usun"
                      >
                        x
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals */}
        <div className="mt-4 flex justify-end">
          <div className="w-72 text-sm">
            <div className="flex justify-between py-1 border-b">
              <span className="text-gray-500">Suma netto:</span>
              <span>{formatPLN(totals.net)}</span>
            </div>
            <div className="flex justify-between py-1 border-b">
              <span className="text-gray-500">Suma VAT:</span>
              <span>{formatPLN(totals.vat)}</span>
            </div>
            <div className="flex justify-between py-2 bg-[var(--maroon)] text-white px-3 rounded mt-1 font-semibold">
              <span>Do zaplaty:</span>
              <span>{formatPLN(totals.gross)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="bordered" onClick={onClose}>
            Anuluj
          </Button>
          <Button variant="default" onClick={openInvoicePreview}>
            Podglad i druk PDF
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default GenerateInvoiceModal;
